import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import BACKEND_URL from 'constants/backendUrl';
import Application from 'core/models/application';
import Task from 'core/models/task';
import { DELETE, GET, POST, PUT } from 'utils/http';

// Couche d'accès « état serveur » des candidatures via TanStack Query.
// Le cache remplace l'ancien flag Redux `alreadyFetched`
// (cf. applicationsAlreadyFetchedSelector, désormais obsolète) et
// l'invalidation de cette clé remplace les actions Set/Add/Update/Delete + refetch.
export const applicationsQueryKey = ['applications'] as const;

interface ApplicationsResponse {
    applications: Application[];
}

// Endpoint GET réutilisé tel quel depuis l'ancien applicationsFetcher (ApplicationsProvider).
export const fetchApplications = async (): Promise<Application[]> => {
    const response = (await GET(`${BACKEND_URL}/api/applications`)) as ApplicationsResponse;

    return response.applications ?? [];
};

export const useApplicationsQuery = () =>
    useQuery({
        queryKey: applicationsQueryKey,
        queryFn: fetchApplications,
    });

// applicationsSelector(state) -> useApplications()
export const useApplications = () => useApplicationsQuery();

// useApplicationById(id) <- applicationByIdSelector(state, id)
export const useApplicationById = (id: string | undefined) =>
    useQuery({
        queryKey: applicationsQueryKey,
        queryFn: fetchApplications,
        select: (applications: Application[]) =>
            applications.find((application) => application.id === id),
        enabled: Boolean(id),
    });

// useApplicationByContactId(contactId) <- applicationByContactIdSelector(state, contactId)
export const useApplicationByContactId = (contactId: string) =>
    useQuery({
        queryKey: applicationsQueryKey,
        queryFn: fetchApplications,
        select: (applications: Application[]) =>
            applications.filter((application) => {
                const { contacts } = application;

                if (!contacts || !contacts.length) {
                    return false;
                }

                return contacts.map((a) => a.id).includes(contactId);
            }),
        enabled: Boolean(contactId),
    });

// useContactIdsWithApplication() <- contactIdsWithApplicationSelector(state)
export const useContactIdsWithApplication = () =>
    useQuery({
        queryKey: applicationsQueryKey,
        queryFn: fetchApplications,
        select: (applications: Application[]) => {
            const contactIds: string[] = [];
            applications.forEach(({ contacts }) => {
                if (contacts && contacts.length) {
                    contacts.forEach(({ id }) => {
                        if (!contactIds.includes(id)) {
                            contactIds.push(id);
                        }
                    });
                }
            });

            return contactIds;
        },
    });

// À appeler après une mutation pour resynchroniser la liste depuis le serveur.
export const useInvalidateApplications = () => {
    const queryClient = useQueryClient();

    return () => queryClient.invalidateQueries({ queryKey: applicationsQueryKey });
};

// --- Mutations (un hook par action d'écriture de l'ancien reducer) ---

// AddApplication <- POST /api/application (création unitaire)
// L'API renvoie { application, task? } ; on conserve la payload telle quelle.
export type CreateApplicationInput = Record<string, unknown>;

export interface CreateApplicationResponse {
    application: Application;
    task?: Task;
}

export const useCreateApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: CreateApplicationInput) =>
            POST<CreateApplicationResponse>(`${BACKEND_URL}/api/application`, input),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: applicationsQueryKey }),
    });
};

// AddApplication (import en masse) <- POST /api/applications
export interface CreateApplicationsResponse {
    createdApplications: Application[];
}

export const useCreateApplicationsMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (applications: Record<string, unknown>[]) =>
            POST<CreateApplicationsResponse>(`${BACKEND_URL}/api/applications`, { applications }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: applicationsQueryKey }),
    });
};

// UpdateApplication <- PUT /api/application/:id
export interface UpdateApplicationResponse {
    application: Application;
}

export const useUpdateApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
            PUT<UpdateApplicationResponse>(`${BACKEND_URL}/api/application/${id}`, input),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: applicationsQueryKey }),
    });
};

// UpdateApplication (rapport / clôture) <- POST /api/application/mark-as-complete
export const useMarkApplicationAsCompleteMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
            POST<UpdateApplicationResponse>(`${BACKEND_URL}/api/application/mark-as-complete`, {
                ...input,
                id,
            }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: applicationsQueryKey }),
    });
};

// DeleteApplication <- DELETE /api/application/:id
export const useDeleteApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (applicationId: string) =>
            DELETE(`${BACKEND_URL}/api/application/${applicationId}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: applicationsQueryKey }),
    });
};
